import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './PersonalID.module.css';

export default function PersonalID() {
  const { currentUser } = useAuth();

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        {currentUser && (
          <div className={styles.userButtonContainer}>
            <Link to="/user-profile" className={styles.viewProfileButton}>
              View my Public ID
            </Link>
          </div>
        )}

        <h1 className={styles.title}>MyPeopleDatabase Personal ID</h1>

        <div className={styles.subtitle}>Your Public ID - Own Your Digital Identity</div>

        <div className={styles.narrative}>
          <p className={styles.intro}>
            MyPeopleDatabase Personal ID is a revolutionary identity curation program that puts you
            in complete control of your public digital presence. In a world where your identity is
            fragmented across countless platforms, MPD Public ID provides a single, unified, and
            verifiable source of truth for who you are.
          </p>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>What is MPD Public ID?</h2>
            <p>
              Your MPD Public ID is a unique, persistent identifier that you own and control. It
              serves as your authoritative digital identity card, containing the information you
              choose to share with the world. Unlike social media profiles that platforms control,
              your MPD Public ID belongs entirely to you.
            </p>
            <p>
              Each user receives a public profile URL (e.g.,{' '}
              <code className={styles.code}>mypeopledatabase.com/profile/your-id</code>) that they
              can share as their official digital identity. This profile becomes your verified
              presence on the internet.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Identity Curation Features</h2>
            <ul className={styles.featureList}>
              <li>
                <strong>Complete Control:</strong> You decide what information appears on your
                public profile—name, photo, contact details, social media links, and professional
                information.
              </li>
              <li>
                <strong>Verified Identity:</strong> Your MPD Public ID is tied to your authenticated
                account, providing a level of verification that standalone profiles cannot offer.
              </li>
              <li>
                <strong>Portable Identity:</strong> Take your identity with you wherever you go.
                Share your MPD Public ID on business cards, email signatures, websites, and social
                media.
              </li>
              <li>
                <strong>Privacy First:</strong> Choose to share as much or as little as you want.
                Keep sensitive information private while maintaining a professional public presence.
              </li>
              <li>
                <strong>Lifetime Access:</strong> Your identity isn't subject to platform changes,
                acquisitions, or shutdowns. As long as you maintain your account, your Public ID
                remains stable and accessible.
              </li>
              <li>
                <strong>Integration Ready:</strong> Connect your Public ID with other services and
                platforms. Use it as a single source of truth for your professional and personal
                networks.
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Why Identity Curation Matters</h2>
            <p>
              In the modern digital landscape, your identity is scattered across dozens of
              platforms— LinkedIn, Twitter, Instagram, personal websites, and more. Each platform
              controls how you present yourself, what data they collect, and who can access it.
            </p>
            <p>
              <strong>The MPD Public ID Program changes this paradigm:</strong>
            </p>
            <ul className={styles.benefitsList}>
              <li>You own the data, not the platform</li>
              <li>You control the narrative of who you are</li>
              <li>You maintain consistency across all your digital touchpoints</li>
              <li>You preserve your identity independent of any single company's decisions</li>
              <li>You create a permanent, findable record that outlasts temporary social trends</li>
            </ul>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Use Cases for Your Public ID</h2>
            <div className={styles.useCases}>
              <div className={styles.useCase}>
                <h3>Professional Networking</h3>
                <p>
                  Share a single link that contains all your professional information, social
                  profiles, and contact methods. Perfect for business cards, email signatures, and
                  conference networking.
                </p>
              </div>
              <div className={styles.useCase}>
                <h3>Personal Branding</h3>
                <p>
                  Build and maintain your personal brand with a stable, professional profile that
                  you fully control. Update your information once and it's reflected everywhere
                  you've shared your Public ID.
                </p>
              </div>
              <div className={styles.useCase}>
                <h3>Digital Legacy</h3>
                <p>
                  Create a lasting digital presence that represents you authentically. Your Public
                  ID serves as a permanent record of your digital identity, independent of shifting
                  social media platforms.
                </p>
              </div>
              <div className={styles.useCase}>
                <h3>Connection Hub</h3>
                <p>
                  Consolidate all your ways of being contacted into one place. Let people reach you
                  via email, phone, or social media—all from your centralized Public ID profile.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Getting Started</h2>
            <p>
              Your MPD Public ID is automatically created when you sign up for MyPeopleDatabase.
              Simply update your profile with the information you want to share publicly, and start
              sharing your unique profile URL with the world.
            </p>
            <p>
              <strong>Remember:</strong> You are in complete control. Update your public profile
              anytime, share as much or as little as you want, and maintain your digital identity on
              your terms.
            </p>
          </div>

          <div className={styles.callToAction}>
            <h2>Ready to Own Your Identity?</h2>
            <p>Join MyPeopleDatabase today and claim your permanent, portable digital identity.</p>
            <div className={styles.ctaButtons}>
              <Link to="/signup" className={styles.primaryButton}>
                Create Your Public ID
              </Link>
              <Link to="/about" className={styles.secondaryButton}>
                Learn More About MPD
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
